import React, {
    useRef,
    useCallback,
    useEffect,
    useState,
    Dispatch,
    SetStateAction,
} from 'react';
import {curry} from 'ramda';
import wrap from 'wraptile';
import {useLocalStorage} from 'react-use';
import useResizeObserver from 'use-resize-observer';
import {ProgressivePlayer} from '@accurate-player/accurate-player-progressive';
import {
    HotkeyPlugin,
    PointPlugin,
    SmoothTimeUpdatePlugin,
    SmoothTimeUpdateEventType,
    HotkeyConfiguration,
    VttSubtitlePlugin,
    VttSubtitle,
} from '@accurate-player/accurate-player-plugins';
import {
    FrameRate,
    PlayerEventType,
    Player,
    TimeFormat,
    SeekMode,
    PlayerStatusChangedEvent,
} from '@accurate-player/accurate-player-core';
import {
    ifSubtitlePlugin,
    addPlayerListenerWhilePause,
    addPlayerListenerWhilePlay,
    addSmoothPluginListenerUntilFrame,
    addPlayerListenerWhileDefaultPlaybackrate,
} from '@pluto/player-check/player-check';
import {
    onFullscreen,
    onTimeStep,
    onToggleMute,
} from '@pluto/player-controls/player-controls';
import {OnBooleanSetState, OnNumberSetState} from '@pluto/@types';
import {noop} from '@pluto/utils';
import {
    SelectNextPreviousMarker,
    ToggleSelectedMarker,
} from 'VideoAudioQC/Timeline/Redux/TimelineActions';
import {
    getSelectedTimelineRow,
    onGoToNextPrevious,
} from 'VideoAudioQC/Timeline/TimelineUtils';
import {Item} from 'VideoAudioQC/QCMedia/Redux/QCMediaActions';
import {SetActiveTab} from 'VideoAudioQC/QCTabs/Redux/QCTabsActions';
import {TabIndex} from 'VideoAudioQC/QCTabs/QCTabsTypes';
import {TimelineMarker, TimelineRowType} from 'VideoAudioQC/Timeline/TimelineTypes';
import {batch} from 'react-redux';
import {EnabledSubtitles} from '../Timeline/SubtitleTracks/Redux/SubtitleTracksReducer';
import AccuratePlayer from './AccuratePlayer';
import {
    PlayerRef,
    ProgressivePlayerOrNull,
    PlayerOrNull,
    AdPreview,
} from './AccuratePlayerTypes';
import {DisableAllSubtitles} from '../Timeline/SubtitleTracks/Redux/SubtitleTracksActions';
import {
    SetPlayerStatus,
    SetAdPreview,
    AdPreviewOrUndefined,
    SetShowHotkeys,
} from './Redux/AccuratePlayerActions';

export interface AccuratePlayerContainerProps {
    item: Item;
    subtitles: VttSubtitle[];
    enabledSubtitles: EnabledSubtitles;
    adPreviews?: AdPreview[];
    adOnPlayingFrame?: TimelineMarker;
    onPlayerCreated: (player: ProgressivePlayer) => void;
    onPlayerDestroyed: () => void;
    setPlayerStatusAction: SetPlayerStatus;
    disableAllSubtitles: DisableAllSubtitles;
    setAdPreviews: SetAdPreview;
    setShowHotkeys: SetShowHotkeys;
    showHotkeys: boolean;
    showSafeMargins: boolean;
    selectNextPreviousMarker: SelectNextPreviousMarker;
    setActiveTab: SetActiveTab;
    toggleSelectedMarker: ToggleSelectedMarker;
    onSetIn: () => void;
    onSetOut: () => void;
    onGoToFrame: (frame: number) => void;
    onCreateQCNote: (rowId: string) => void;
    onCreateAdBreak: (rowId: string) => void;
    onCreateMarkup: (rowId: string) => void;
    activeTab: TabIndex;
    selectedRowId: string;
    selectedType: TimelineRowType;
    selectedMarkerId: string;
    qcReportRowId: string;
    adBreaksRowId: string;
    markupRowId: string;
    jobDeliveryDate: string;
    onIncreaseSpeed: () => void;
    onDecreaseSpeed: () => void;
    onSpeedReset: () => void;
    onGetSnapshot: () => void;
}

export type OnHotkeysSetState = React.Dispatch<SetStateAction<HotkeyConfiguration[]>>;

type VideoChangedProps = {
    licenseKey: string;
    playerRef: PlayerRef;
    subtitles: VttSubtitle[];
    onPlayerCreated: (player: ProgressivePlayer) => void;
    onPlayerDestroyed: () => void;
    setPlayerStatusAction: SetPlayerStatus;
    handleDisableSubtitles: () => void;
    loadPlayerInfo: (playerRef: PlayerRef) => void;
    setStickyVolume: (value: number) => void;
    setStickyMute: (value: boolean) => void;
    setHotkeys: OnHotkeysSetState;
    handleNextMarker: () => void;
    handlePreviousMarker: () => void;
    setShowHotkeys: (value: boolean) => void;
    handleCreateQCNote: () => void;
    handleCreateAdBreak: () => void;
    handleCreateMarkup: () => void;
    handleMoveMarkerStart: () => void;
    handleMoveMarkerEnd: () => void;
    handleGoToStart: () => void;
    handleGoToEnd: () => void;
    handleResetSelectedMarker: () => void;
    setAspectRatio: OnNumberSetState;
    jobDeliveryDate: string;
    onIncreaseSpeed: () => void;
    onDecreaseSpeed: () => void;
    onSpeedReset: () => void;
    onGetSnapshot: () => void;
};

export const getVTTSubtitlePlugin = (player: PlayerOrNull): VttSubtitlePlugin => {
    return player?.api?.plugin[VttSubtitlePlugin.Name] as VttSubtitlePlugin;
};

export const updateEnabledSubtitles = wrap(
    (player: Player, enabled: EnabledSubtitles) => {
        const vttSubtitlePlugin = getVTTSubtitlePlugin(player);

        handleEnabledSubtitles(vttSubtitlePlugin, vttSubtitlePlugin, enabled);
    }
);

export const handleEnabledSubtitles = ifSubtitlePlugin(
    (vttSubtitlePlugin: VttSubtitlePlugin, enabledSubtitles: EnabledSubtitles) => {
        const allSubtitles = vttSubtitlePlugin.vttSubtitles;

        if (!allSubtitles) return;

        for (const subtitle of allSubtitles) {
            const {enabled, src} = subtitle;
            const enable = Boolean(enabledSubtitles[src]);

            if (enable !== enabled) vttSubtitlePlugin.toggleVttSubtitle(subtitle);
        }
    }
);

export type VideoFileProps = {
    src: string;
    duration: number;
    frameRate: FrameRate;
    dropFrame: boolean;
    volume: number;
    stickyMute: boolean;
};

export const handleVideoFileChange = wrap(
    (props: VideoFileProps, playerRef: PlayerRef) => {
        const {src, duration, frameRate, dropFrame, volume, stickyMute} = props;
        const player = playerRef.current;

        if (!player || !player.api) return;

        player.api.loadVideoFile({
            src,
            duration,
            channelCount: 2,
            enabled: false,
            frameRate,
            dropFrame,
        });

        player.api.setVolume(volume);

        if (stickyMute) {
            player.api.mute();
        } else {
            player.api.unmute();
        }
    }
);

function createPointPlugin(player: ProgressivePlayer) {
    new PointPlugin(player);
}

export const openHotkeyMenu = wrap((setShowHotkeys: (value: boolean) => void) => {
    setShowHotkeys(true);
});

const handleVideoChanged = curry(
    (props: VideoChangedProps, node: HTMLVideoElement): void => {
        const {
            licenseKey,
            playerRef,
            onPlayerCreated,
            onPlayerDestroyed,
            loadPlayerInfo,
            setPlayerStatusAction,
            setStickyVolume,
            setStickyMute,
            setHotkeys,
            subtitles,
            setShowHotkeys,
            setAspectRatio,
        } = props;

        if (node === null && playerRef.current !== null) {
            playerRef.current.removeAllListeners();
            playerRef.current.destroy();
            onPlayerDestroyed();
            playerRef.current = null;
        }

        if (node !== null && playerRef.current === null) {
            const progressivePlayer = new ProgressivePlayer(node, licenseKey);

            const hotkeyPlugin = new HotkeyPlugin(progressivePlayer);
            setPlayerHotkeys(hotkeyPlugin, props);

            new SmoothTimeUpdatePlugin(progressivePlayer);

            if (subtitles?.length > 0) {
                const vttSubtitlePlugin = new VttSubtitlePlugin(progressivePlayer);
                vttSubtitlePlugin.createVttSubtitles(subtitles);
            }

            createPointPlugin(progressivePlayer);

            onPlayerCreated(progressivePlayer);

            playerRef.current = progressivePlayer;
            loadPlayerInfo(playerRef);
            initEventListeners(playerRef.current, {
                setPlayerStatusAction,
                setStickyVolume,
                setStickyMute,
                setAspectRatio,
            });

            setOpenHotkeyMenuHotkey(hotkeyPlugin, openHotkeyMenu(setShowHotkeys));
            setHotkeys(hotkeyPlugin.currentConfigurations);
        }
    }
);

function setOpenHotkeyMenuHotkey(hotkeyPlugin: HotkeyPlugin, setShowHotkeys: () => void) {
    hotkeyPlugin.setHotkey('shift+/', setShowHotkeys, 'Show hotkeys');
}

function setPlayerHotkeys(hotkeyPlugin: HotkeyPlugin, props: VideoChangedProps) {
    const {
        playerRef,
        handleDisableSubtitles,
        handlePreviousMarker,
        handleNextMarker,
        handleCreateQCNote,
        handleCreateAdBreak,
        handleCreateMarkup,
        handleMoveMarkerStart,
        handleMoveMarkerEnd,
        handleGoToStart,
        handleGoToEnd,
        handleResetSelectedMarker,
        jobDeliveryDate,
        onIncreaseSpeed,
        onDecreaseSpeed,
        onSpeedReset,
        onGetSnapshot,
    } = props;

    hotkeyPlugin.setHotkey('m', onToggleMute(playerRef), 'Mute/Unmute');

    hotkeyPlugin.setHotkey('f', onFullscreen(playerRef), 'Toggle fullscreen');

    hotkeyPlugin.setHotkey(
        'ctrl+shift+down',
        onTimeStep(playerRef, 5, SeekMode.RelativeBackward, TimeFormat.Seconds),
        'Step 5 Seconds Backward'
    );

    hotkeyPlugin.setHotkey(
        'ctrl+shift+up',
        onTimeStep(playerRef, 5, SeekMode.RelativeForward, TimeFormat.Seconds),
        'Step 5 Seconds Forward'
    );

    hotkeyPlugin.setHotkey('d', handleDisableSubtitles, 'Disable all Subtitle Tracks');

    setMarkerHotkeys(
        {
            handleCreateQCNote,
            handleCreateAdBreak,
            handleCreateMarkup,
            handleMoveMarkerStart,
            handleMoveMarkerEnd,
            handleGoToStart,
            handleGoToEnd,
            handleResetSelectedMarker,
            jobDeliveryDate,
            onIncreaseSpeed,
            onDecreaseSpeed,
            onSpeedReset,
            onGetSnapshot,
        },
        hotkeyPlugin
    );

    hotkeyPlugin.setHotkey('ctrl+shift+c', handlePreviousMarker, 'Go to previous item');

    hotkeyPlugin.setHotkey('shift+c', handleNextMarker, 'Go to next item');

    hotkeyPlugin.setHotkeysFilter(hotkeyFilter);
}

export const BUTTON = 'BUTTON';
export const SELECT = 'SELECT';
export const INPUT = 'INPUT';
export const TEXTAREA = 'TEXTAREA';

export const ignoredHotkeyElements = [INPUT, SELECT, TEXTAREA];

export const isIgnoredHotkeyElement = (tagName: string) => {
    return ignoredHotkeyElements.includes(tagName);
};

export const isMaterialSelectElement = (element: HTMLElement) => {
    const {classList} = element;

    return classList ? classList.contains('MuiSelect-select') : false;
};

export const isButtonElement = (element: HTMLElement) => {
    const {tagName} = element;

    if (tagName === BUTTON) return true;

    if (tagName === INPUT) {
        const {type} = element as HTMLInputElement;

        if (type === 'button' || type === 'checkbox') return true;
    }

    if (element.hasAttribute('role') && !isMaterialSelectElement(element)) {
        const role = element.getAttribute('role');

        if (role === 'button' || role === 'checkbox') return true;
    }

    return false;
};

export const isMuiPopupOrDialogTarget = (event: KeyboardEvent) => {
    return Boolean(
        event.composedPath().find((target) => {
            const {classList} = target as HTMLElement;
            return classList
                ? classList.contains('MuiDialog-container') ||
                      classList.contains('MuiPopover-paper')
                : false;
        })
    );
};

export const hotkeyFilter = (event: KeyboardEvent) => {
    const element = event.target as HTMLElement;
    const {tagName} = element;
    const isDialogOrPopupShown = isMuiPopupOrDialogTarget(event);
    const isMaterialSelect = isMaterialSelectElement(element);
    const isButton = isButtonElement(element);

    if (isDialogOrPopupShown || isMaterialSelect) return false;

    if (!isIgnoredHotkeyElement(BUTTON) && isButton)
        // special case for buttons since we consider some inputs and elements with button role as buttons as well.
        return true;

    return !isIgnoredHotkeyElement(tagName) && !element.isContentEditable;
};

const useVideoRef = (props: VideoChangedProps) => {
    const {onPlayerCreated, onPlayerDestroyed, licenseKey} = props;
    const onVideoChanged = handleVideoChanged(props);
    const setRef = useCallback(onVideoChanged, [
        onPlayerCreated,
        onPlayerDestroyed,
        licenseKey,
    ]);

    return [setRef];
};

type StatusActions = {
    setPlayerStatusAction: SetPlayerStatus;
    setStickyVolume: (value: number) => void;
    setStickyMute: (value: boolean) => void;
    setAspectRatio: OnNumberSetState;
};

export const onStatusChange = curry(
    (actions: StatusActions, event: PlayerStatusChangedEvent) => {
        const {setPlayerStatusAction, setStickyVolume, setStickyMute} = actions;
        setStickyVolume(event.status.volume);
        setStickyMute(event.status.muted);
        setPlayerStatusAction(event.status);
    }
);

export const onPlayerLoaded = wrap(
    (player: ProgressivePlayer, setAspectRatio: OnNumberSetState) => {
        const element = (player.api?.master as unknown) as HTMLVideoElement;

        if (element) {
            const {videoHeight, videoWidth} = element;
            const videoRatio = videoWidth / videoHeight;

            setAspectRatio(videoRatio);
        }
    }
);

const initEventListeners = (player: ProgressivePlayer, actions: StatusActions) => {
    const {setAspectRatio} = actions;

    const handleStatusChange = onStatusChange(actions);
    const handlePlayerLoaded = onPlayerLoaded(player, setAspectRatio);

    player.addEventListener(PlayerEventType.StatusChanged, handleStatusChange);
    player.addEventListener(PlayerEventType.Loaded, handlePlayerLoaded);
};

const initialVolume = 0.5;
const unmuted = false;

export const onDisableSubtitles = wrap((disableSubtitles: DisableAllSubtitles) => {
    disableSubtitles();
});

export type AdPlaybackStatus =
    | 'not-started'
    | 'pre-ad'
    | 'ad'
    | 'post-ad'
    | 'done'
    | 'cancelled';

type OnAdPlaybackStatusSetState = Dispatch<SetStateAction<AdPlaybackStatus>>;

export const AD_LENGTH = 3000;

export const onAdAborted = (status: AdPlaybackStatus): AdPlaybackStatus => {
    if (status === 'ad') return 'cancelled';

    return status;
};

export const onAdAbortedWhilePlaying = (status: AdPlaybackStatus): AdPlaybackStatus => {
    if (status === 'pre-ad' || status === 'post-ad') return 'cancelled';

    return status;
};

export type HideAdProps = {
    player: Player;
    setShowAd: OnBooleanSetState;
    setStatus: OnAdPlaybackStatusSetState;
    handlePlayerStatusChange: (event: PlayerStatusChangedEvent) => void;
};

export const onHideAd = wrap(async (props: HideAdProps) => {
    const {player, setShowAd, setStatus, handlePlayerStatusChange} = props;

    player.removeEventListener(PlayerEventType.StatusChanged, handlePlayerStatusChange);
    await player.api.seek({
        time: 1,
        mode: SeekMode.RelativeForward,
        format: TimeFormat.Frame,
    });

    setStatus('post-ad');
    setShowAd(false);
});

export const clearAdTimeout = (id: number) => {
    clearTimeout(id);

    return -1;
};

const onCleanupAd = wrap(
    (
        setShowAd: OnBooleanSetState,
        setStatus: OnAdPlaybackStatusSetState,
        setAdTimeout: OnNumberSetState
    ) => {
        setShowAd(false);
        setAdTimeout(clearAdTimeout);
        setStatus(onAdAborted);
    }
);

const onCleanUpAdWhilePlaying = wrap(
    (
        setShowAd: OnBooleanSetState,
        setStatus: OnAdPlaybackStatusSetState,
        setAdTimeout: OnNumberSetState
    ) => {
        setShowAd(false);
        setAdTimeout(clearAdTimeout);
        setStatus(onAdAbortedWhilePlaying);
    }
);

export type HandleShowAdProps = {
    player: Player;
    setShowAd: OnBooleanSetState;
    setStatus: OnAdPlaybackStatusSetState;
    setAdTimeout: OnNumberSetState;
};

export const handleShowAd = (props: HandleShowAdProps) => {
    const {player, setShowAd, setStatus, setAdTimeout} = props;
    const handleCleanupAd = onCleanupAd(setShowAd, setStatus, setAdTimeout);
    const handlePlayerStatusChange = addPlayerListenerWhilePause(player, handleCleanupAd);

    setShowAd(true);

    const handleHideAd = onHideAd({
        player,
        setShowAd,
        setStatus,
        handlePlayerStatusChange,
    });
    const id = window.setTimeout(handleHideAd, AD_LENGTH);
    setAdTimeout(id);
};

export type PlayVideoClipProps = {
    player: Player;
    start: number;
    end: number;
    status: AdPlaybackStatus;
    setStatus: OnAdPlaybackStatusSetState;
    setShowAd: OnBooleanSetState;
    setAdTimeout: OnNumberSetState;
};

export const playVideoClip = async (props: PlayVideoClipProps) => {
    const {player, start, end, status, setStatus, setShowAd, setAdTimeout} = props;
    const plugin = player.api.plugin[
        SmoothTimeUpdatePlugin.Name
    ] as SmoothTimeUpdatePlugin;

    await player.api.pause();
    await player.api.seek({
        time: start,
        mode: SeekMode.Absolute,
        format: TimeFormat.Frame,
    });
    await player.api.play();

    const handleCleanupAd = onCleanUpAdWhilePlaying(setShowAd, setStatus, setAdTimeout);
    const cleanupListeners = () => {
        plugin.removeEventListener(
            SmoothTimeUpdateEventType.TimeUpdate,
            handleVideoTimeUpdate
        );
        player.removeEventListener(
            PlayerEventType.UserPause,
            handlePlayerUserPauseChange
        );
        player.removeEventListener(
            PlayerEventType.StatusChanged,
            handlePlayerStatusChange
        );

        handleCleanupAd();
    };

    const handlePlayerUserPauseChange = addPlayerListenerWhilePlay(
        player,
        cleanupListeners
    );

    const handlePlayerStatusChange = addPlayerListenerWhileDefaultPlaybackrate(
        player,
        cleanupListeners
    );

    const stopPlayback = () => {
        plugin.removeEventListener(
            SmoothTimeUpdateEventType.TimeUpdate,
            handleVideoTimeUpdate
        );
        player.removeEventListener(
            PlayerEventType.UserPause,
            handlePlayerUserPauseChange
        );
        player.removeEventListener(
            PlayerEventType.StatusChanged,
            handlePlayerStatusChange
        );

        player.api.pause({
            time: end,
            mode: SeekMode.Absolute,
            format: TimeFormat.Frame,
        });

        if (status === 'pre-ad') {
            setStatus('ad');
        } else {
            setStatus('done');
        }
    };

    const handleVideoTimeUpdate = addSmoothPluginListenerUntilFrame(
        plugin,
        end,
        stopPlayback
    );
};

const CLIP_LENGTH_SECONDS = 5;

export type AdPreviewProps = {
    playerRef: PlayerRef;
    adPreviews: AdPreview[];
    clipLength: number;
    currentAd: number;
    status: AdPlaybackStatus;
    showAd: boolean;
    adTimeout: number;
    setShowAd: OnBooleanSetState;
    setCurrentAd: OnNumberSetState;
    setStatus: OnAdPlaybackStatusSetState;
    setAdTimeout: OnNumberSetState;
    setAdPreviews: SetAdPreview;
};

export type PrePostClipProps = {
    player: Player;
    clip: AdPreview;
    clipLength: number;
    status: AdPlaybackStatus;
    setStatus: OnAdPlaybackStatusSetState;
    setShowAd: OnBooleanSetState;
    setAdTimeout: OnNumberSetState;
};

export const playPreClip = (props: PrePostClipProps) => {
    const {player, clip, clipLength, status, setStatus, setShowAd, setAdTimeout} = props;
    const {frame} = clip;
    const start = Math.max(frame - clipLength, 0);
    const end = frame;

    if (start === 0) {
        setStatus('ad');
        return;
    }

    playVideoClip({
        player,
        start,
        end,
        status,
        setStatus,
        setShowAd,
        setAdTimeout,
    });
};

export const playPostClip = (props: PrePostClipProps) => {
    const {player, clip, clipLength, status, setStatus, setShowAd, setAdTimeout} = props;
    const duration = Number(player.api.getDuration(TimeFormat.Frame));
    const {frame} = clip;
    const start = frame + 1;
    const end = Math.min(frame + clipLength, duration);

    if (end === duration) {
        setStatus('done');
        return;
    }

    playVideoClip({
        player,
        start,
        end,
        status,
        setStatus,
        setShowAd,
        setAdTimeout,
    });
};

export const handleStatusChange = (player: Player, props: AdPreviewProps) => {
    const {
        adPreviews,
        clipLength,
        currentAd,
        status,
        setShowAd,
        setCurrentAd,
        setStatus,
        setAdTimeout,
        setAdPreviews,
    } = props;

    const clip = adPreviews ? adPreviews[currentAd] : ({} as AdPreview);

    switch (status) {
        case 'not-started':
            // Set playbackrate to 1 before starting preview.
            if (player.api.getStatus().playbackRate !== 1) player.api.setPlaybackRate(1);

            setStatus('pre-ad');
            break;
        case 'pre-ad':
            playPreClip({
                player,
                clip,
                clipLength,
                status,
                setStatus,
                setShowAd,
                setAdTimeout,
            });
            break;
        case 'ad':
            handleShowAd({
                player,
                setShowAd,
                setStatus,
                setAdTimeout,
            });
            break;
        case 'post-ad':
            playPostClip({
                player,
                clip,
                clipLength,
                status,
                setStatus,
                setShowAd,
                setAdTimeout,
            });
            break;
        case 'done':
            if (adPreviews && adPreviews.length - 1 > currentAd) {
                setCurrentAd(currentAd + 1);
                setStatus('not-started');
            } else {
                setAdPreviews(undefined);
            }
            break;
        case 'cancelled':
            setAdPreviews(undefined);
            break;
    }
};

export const handleAdPreview = wrap((props: AdPreviewProps) => {
    const {playerRef, currentAd, showAd, adTimeout, setShowAd} = props;
    const player = playerRef?.current;

    if (!player) return;

    if (currentAd === -1) {
        if (showAd) {
            clearTimeout(adTimeout);
            setShowAd(false);
        }

        return;
    }
    handleStatusChange(player, props);
});

export const ongoingPreview = (status: AdPlaybackStatus): boolean => {
    return status === 'pre-ad' || status === 'ad' || status === 'post-ad';
};

export type HandleAdBreakChangeProps = {
    playerRef: PlayerRef;
    adPreviews: AdPreview[];
    adTimeout: number;
    status: AdPlaybackStatus;
    setCurrentAd: OnNumberSetState;
    setShowAd: OnBooleanSetState;
    setStatus: OnAdPlaybackStatusSetState;
};

export const handleAdBreaksChange = wrap((props: HandleAdBreakChangeProps) => {
    const {
        playerRef,
        adPreviews,
        adTimeout,
        status,
        setCurrentAd,
        setShowAd,
        setStatus,
    } = props;
    const player = playerRef.current;

    if (!player) return;

    if (ongoingPreview(status)) {
        player.api.pause();
    }

    if (adPreviews && adPreviews.length > 0) {
        setCurrentAd(0);
    } else {
        setCurrentAd(-1);
    }

    if (adTimeout >= 0) {
        clearTimeout(adTimeout);
    }

    setShowAd(false);
    setStatus('not-started');
});

export type UsePreviewProps = {
    playerRef: PlayerRef;
    adPreviews: AdPreviewOrUndefined;
    clipLength: number;
    showAd: boolean;
    setShowAd: OnBooleanSetState;
    setAdPreviews: SetAdPreview;
};

export const usePreviewAdBreaks = (props: UsePreviewProps) => {
    const {playerRef, adPreviews, clipLength, showAd, setShowAd, setAdPreviews} = props;
    const [currentAd, setCurrentAd] = useState(-1);
    const [status, setStatus] = useState<AdPlaybackStatus>('not-started');
    const [adTimeout, setAdTimeout] = useState(-1);

    const onAdBreaksChange = handleAdBreaksChange({
        playerRef,
        adPreviews,
        adTimeout,
        status,
        setCurrentAd,
        setShowAd,
        setStatus,
    });
    useEffect(onAdBreaksChange, [adPreviews]);

    const onAdBreakPreview = handleAdPreview({
        playerRef,
        adPreviews,
        clipLength,
        currentAd,
        status,
        showAd,
        adTimeout,
        setShowAd,
        setCurrentAd,
        setStatus,
        setAdTimeout,
        setAdPreviews,
    });
    useEffect(onAdBreakPreview, [currentAd, status]);
};

export const onCloseHotkeys = wrap((setShowHotkeys: SetShowHotkeys) => {
    setShowHotkeys(false);
});

export const getPointPlugin = (player: PlayerOrNull): PointPlugin => {
    return player?.api?.plugin[PointPlugin.Name] as PointPlugin;
};

export const getHotkeyPlugin = (player: PlayerOrNull): HotkeyPlugin => {
    return player?.api?.plugin[HotkeyPlugin.Name] as HotkeyPlugin;
};

interface MarkerHotkeysProps {
    handleCreateQCNote: () => void;
    handleCreateAdBreak: () => void;
    handleCreateMarkup: () => void;
    handleMoveMarkerStart: () => void;
    handleMoveMarkerEnd: () => void;
    handleGoToStart: () => void;
    handleGoToEnd: () => void;
    handleResetSelectedMarker: () => void;
    jobDeliveryDate: string;
    onIncreaseSpeed: () => void;
    onDecreaseSpeed: () => void;
    onSpeedReset: () => void;
    onGetSnapshot: () => void;
}

function setMarkerHotkeys(props: MarkerHotkeysProps, hotkeyPlugin: HotkeyPlugin) {
    const {
        handleCreateQCNote,
        handleCreateAdBreak,
        handleCreateMarkup,
        handleMoveMarkerStart,
        handleMoveMarkerEnd,
        handleGoToStart,
        handleGoToEnd,
        handleResetSelectedMarker,
        jobDeliveryDate,
        onIncreaseSpeed,
        onDecreaseSpeed,
        onSpeedReset,
        onGetSnapshot,
    } = props;

    hotkeyPlugin.setHotkey(
        'c',
        Boolean(jobDeliveryDate) ? noop : handleCreateQCNote,
        'Add Note'
    );

    hotkeyPlugin.setHotkey(
        'v',
        Boolean(jobDeliveryDate) ? noop : handleCreateAdBreak,
        'Add Breakpoint'
    );

    hotkeyPlugin.setHotkey(
        'b',
        Boolean(jobDeliveryDate) ? noop : handleCreateMarkup,
        'Add Markup'
    );

    hotkeyPlugin.setHotkey('s', handleMoveMarkerStart, 'Move Start to CTI');

    hotkeyPlugin.setHotkey('e', handleMoveMarkerEnd, 'Move End to CTI');

    hotkeyPlugin.setHotkey('shift+s', handleGoToStart, 'Go to Start');

    hotkeyPlugin.setHotkey('shift+e', handleGoToEnd, 'Go to End');

    hotkeyPlugin.setHotkey('esc', handleResetSelectedMarker, 'Deselect item');

    hotkeyPlugin.setHotkey('l', onIncreaseSpeed, 'Increase playback rate');

    hotkeyPlugin.setHotkey('j', onDecreaseSpeed, 'Decrease playback rate');

    hotkeyPlugin.setHotkey('k', onSpeedReset, 'Pause and reset playback rate');

    hotkeyPlugin.setHotkey(
        'n',
        Boolean(jobDeliveryDate) ? noop : onGetSnapshot,
        'Add Snapshot'
    );
}

interface OnUpdateHotkeysProps extends MarkerHotkeysProps {
    playerRef: PlayerRef;
    setMarkerHotkeys: (props: MarkerHotkeysProps, hotkeyPlugin: HotkeyPlugin) => void;
}

export const onUpdateHotkeys = wrap((props: OnUpdateHotkeysProps) => {
    const {playerRef, setMarkerHotkeys} = props;
    const player = playerRef.current;

    if (!player) return;

    const hotkeyPlugin = getHotkeyPlugin(player);

    if (!hotkeyPlugin) return;

    setMarkerHotkeys(props, hotkeyPlugin);
});

export interface OnResetSelectedMarkerProps {
    selectedRowId: string;
    selectedType: TimelineRowType;
    selectedMarkerId: string;
    toggleSelectedMarker: ToggleSelectedMarker;
}

export const onResetSelectedMarker = wrap((props: OnResetSelectedMarkerProps) => {
    const {selectedRowId, selectedType, selectedMarkerId, toggleSelectedMarker} = props;

    if (!selectedMarkerId) return;

    toggleSelectedMarker(selectedMarkerId, selectedRowId, selectedType);
});

export interface HandleOnCreateMarkerProps {
    onCreateMarker: (rowId: string) => void;
    setActiveTab: SetActiveTab;
    rowId: string;
    activeTab: TabIndex;
    markerToCreateInTab: TabIndex;
}

export const dispatchCreateMarkerActions = wrap((props: HandleOnCreateMarkerProps) => {
    const {onCreateMarker, setActiveTab, rowId, activeTab, markerToCreateInTab} = props;

    if (activeTab !== markerToCreateInTab) setActiveTab(markerToCreateInTab);

    onCreateMarker(rowId);
});

export const handleOnCreateMarker = wrap((props: HandleOnCreateMarkerProps) => {
    batch(dispatchCreateMarkerActions(props));
});

type OnSetStartEnd = (
    selectedType: TimelineRowType,
    selectedMarker: TimelineMarker
) => void;

export const handleOnMoveMarker = wrap(
    (onSetStartEnd: OnSetStartEnd, selectedType: TimelineRowType) => {
        const metadata = getSelectedTimelineRow();

        if (!metadata) return;

        const {selection} = metadata;
        const [selected] = selection;

        const {identifier, start, end} = selected;
        const selectedMarker: TimelineMarker = {
            identifier,
            start,
            end,
        };

        onSetStartEnd(selectedType, selectedMarker);
    }
);

export type OnGoToStartEndProps = {
    goToStart: boolean;
    selectedType: TimelineRowType;
    onGoToFrame: (frame: number | string) => void;
};

export const onGoToStartEnd = wrap((props: OnGoToStartEndProps) => {
    const {goToStart, selectedType, onGoToFrame} = props;

    const metadata = getSelectedTimelineRow();

    if (!metadata) return;

    const {selection} = metadata;
    const [selected] = selection;
    const {start, end} = selected;

    if (goToStart || selectedType === TimelineRowType.AdBreak) {
        onGoToFrame(start);
    } else {
        onGoToFrame(end);
    }
});

export const onTogglePlay = wrap((playerRef: PlayerRef) => {
    const player = playerRef.current;

    if (!player) return;

    player.api.togglePlay();
});

export type OnVideoContainerResizeProps = {
    height: number;
    width: number;
    aspectRatio: number;
};

export const onVideoContainerResize = wrap((props: OnVideoContainerResizeProps) => {
    const {height, width, aspectRatio} = props;
    const containerAspectRatio = width / height;

    if (aspectRatio < containerAspectRatio) {
        document.documentElement.style.setProperty(
            '--ap-video-height',
            `${height.toString(10)}px`
        );
        document.documentElement.style.setProperty(
            '--ap-video-width',
            `${(height * aspectRatio).toString(10)}px`
        );
    } else {
        document.documentElement.style.setProperty(
            '--ap-video-height',
            `${(width / aspectRatio).toString(10)}px`
        );
        document.documentElement.style.setProperty(
            '--ap-video-width',
            `${width.toString(10)}px`
        );
    }
});

export const DEFAULT_ASPECT_RATIO = 16 / 9;

function AccuratePlayerContainer(props: AccuratePlayerContainerProps) {
    const {
        adOnPlayingFrame,
        item,
        enabledSubtitles,
        subtitles,
        adPreviews,
        onPlayerCreated,
        onPlayerDestroyed,
        setPlayerStatusAction,
        disableAllSubtitles,
        setAdPreviews,
        showHotkeys,
        showSafeMargins,
        setShowHotkeys,
        selectNextPreviousMarker,
        setActiveTab,
        toggleSelectedMarker,
        onSetIn,
        onSetOut,
        onGoToFrame,
        onCreateQCNote,
        onCreateAdBreak,
        onCreateMarkup,
        activeTab,
        selectedRowId,
        selectedType,
        selectedMarkerId,
        qcReportRowId,
        adBreaksRowId,
        markupRowId,
        jobDeliveryDate,
        onIncreaseSpeed,
        onDecreaseSpeed,
        onSpeedReset,
        onGetSnapshot,
    } = props;

    const {src, duration, frameRate, dropFrame} = item.video;
    const playerRef = useRef<ProgressivePlayerOrNull>(null);
    const licenseKey = process.env.REACT_APP_ACCURATE_PLAYER_VERSION || '';
    const [volume, setStickyVolume] = useLocalStorage('volume', initialVolume);
    const [stickyMute, setStickyMute] = useLocalStorage('mute', unmuted);
    const [aspectRatio, setAspectRatio] = useState<number>(DEFAULT_ASPECT_RATIO);
    const loadPlayerInfo = handleVideoFileChange({
        src,
        duration,
        frameRate,
        dropFrame,
        volume,
        stickyMute,
    });
    const [hotkeys, setHotkeys] = useState<HotkeyConfiguration[]>([]);

    const handleDisableSubtitles = onDisableSubtitles(disableAllSubtitles);
    const handleNextMarker = onGoToNextPrevious({
        next: true,
        selectNextPreviousMarker,
    });
    const handlePreviousMarker = onGoToNextPrevious({
        next: false,
        selectNextPreviousMarker,
    });
    const handleResetSelectedMarker = onResetSelectedMarker({
        selectedRowId,
        selectedType,
        selectedMarkerId,
        toggleSelectedMarker,
    });
    const handleCreateQCNote = handleOnCreateMarker({
        onCreateMarker: onCreateQCNote,
        setActiveTab,
        activeTab,
        rowId: qcReportRowId,
        markerToCreateInTab: TabIndex.QCReport,
    });
    const handleCreateAdBreak = handleOnCreateMarker({
        onCreateMarker: onCreateAdBreak,
        setActiveTab,
        activeTab,
        rowId: adBreaksRowId,
        markerToCreateInTab: TabIndex.AdBreaks,
    });
    const handleCreateMarkup = handleOnCreateMarker({
        onCreateMarker: onCreateMarkup,
        setActiveTab,
        activeTab,
        rowId: markupRowId,
        markerToCreateInTab: TabIndex.Markup,
    });

    const handleMoveMarkerStart = handleOnMoveMarker(onSetIn, selectedType);
    const handleMoveMarkerEnd = handleOnMoveMarker(onSetOut, selectedType);

    const handleGoToStart = onGoToStartEnd({
        goToStart: true,
        selectedType,
        onGoToFrame,
    });
    const handleGoToEnd = onGoToStartEnd({
        goToStart: false,
        selectedType,
        onGoToFrame,
    });

    const updateHotkeys = onUpdateHotkeys({
        playerRef,
        setMarkerHotkeys,
        handleCreateQCNote,
        handleCreateAdBreak,
        handleCreateMarkup,
        handleMoveMarkerStart,
        handleMoveMarkerEnd,
        handleGoToStart,
        handleGoToEnd,
        handleResetSelectedMarker,
        jobDeliveryDate,
        onIncreaseSpeed,
        onDecreaseSpeed,
        onSpeedReset,
        onGetSnapshot,
    });

    useEffect(updateHotkeys, [activeTab, selectedRowId, selectedType, selectedMarkerId]);

    const [setVideoRef] = useVideoRef({
        playerRef,
        licenseKey,
        subtitles,
        onPlayerCreated,
        onPlayerDestroyed,
        setPlayerStatusAction,
        setStickyVolume,
        setStickyMute,
        handleDisableSubtitles,
        loadPlayerInfo,
        setHotkeys,
        handleNextMarker,
        handlePreviousMarker,
        setShowHotkeys,
        handleCreateQCNote,
        handleCreateAdBreak,
        handleCreateMarkup,
        handleMoveMarkerStart,
        handleMoveMarkerEnd,
        handleGoToStart,
        handleGoToEnd,
        handleResetSelectedMarker,
        setAspectRatio,
        jobDeliveryDate,
        onIncreaseSpeed,
        onDecreaseSpeed,
        onSpeedReset,
        onGetSnapshot,
    });

    const onEnabledSubtitlesChange = updateEnabledSubtitles(
        playerRef.current,
        enabledSubtitles
    );
    useEffect(onEnabledSubtitlesChange, [enabledSubtitles]);

    const onInfoChange = handleVideoFileChange(
        {
            src,
            duration,
            frameRate,
            dropFrame,
            volume,
            stickyMute,
        },
        playerRef
    );
    useEffect(onInfoChange, [src, frameRate, dropFrame, playerRef]);

    const clipLength = Math.round(
        (CLIP_LENGTH_SECONDS * frameRate.numerator) / frameRate.denominator
    );
    const [showAd, setShowAd] = useState(false);
    usePreviewAdBreaks({
        playerRef,
        adPreviews,
        clipLength,
        showAd,
        setShowAd,
        setAdPreviews,
    });

    const handleTogglePlay = onTogglePlay(playerRef);

    const handleCloseHotkeys = onCloseHotkeys(setShowHotkeys);

    const {ref, height = 1, width = 1} = useResizeObserver<HTMLDivElement>();

    const handleVideoResize = onVideoContainerResize({
        height,
        width,
        aspectRatio,
    });

    useEffect(handleVideoResize, [height, width, aspectRatio]);

    return (
        <AccuratePlayer
            videoContainerRef={ref}
            togglePlay={handleTogglePlay}
            setVideoRef={setVideoRef}
            showAd={showAd}
            showHotkeys={showHotkeys}
            showSafeMargins={showSafeMargins}
            videoAspectRatio={aspectRatio}
            hotkeys={hotkeys}
            closeHotkeys={handleCloseHotkeys}
            adOnPlayingFrame={adOnPlayingFrame}
        />
    );
}

export default AccuratePlayerContainer;
